/* MLMT 2027 — the cart drawer's scripts: the tabs + favorites panel and
   THE ONE WRITER of the subtotal.

   12 Sep 2026 — both used to be inline <script> blocks in
   snippets/cart-drawer.liquid (no Liquid, only DOM queries and listeners).
   Load order matters and is the same as inline: this file is deferred and
   sits BEFORE theme.js in layout/theme.liquid, so the 'cart:built' listener
   below is registered before theme.CartDrawer's first build resolves.
   Each IIFE returns early when its markup is absent. Code is verbatim.

   18 Sep 2026 — the tabs + favorites block is no longer verbatim. Three
   things changed, all from Jordan's three asks that day (recorded at the
   sections they touch): a favourite added to the cart now LEAVES the
   wishlist (section 4, reversing the 5 Sep decision), every cart line
   carries a heart that saves it for later and takes it out of the bag
   (section 5, new), and the store behind both is mirrored to a cookie
   (snippets/mlmt-wishlist-core). Doing that safely meant one structural
   change: every listener in the block now binds by DELEGATION on
   document, and the drawer's elements are re-queried by bind() — at
   load and again on 'shopify:section:load', because the header section
   renders this drawer and the theme editor replaces the whole node when
   it re-renders the header. Before, the block captured the elements once
   and a re-rendered drawer was left with dead tabs. */

/* ══ snippets/cart-drawer.liquid — tabs + favorites ══ */
  (function(){
    'use strict';

    /* Idempotent: the document-level listeners below are registered once
       per page, whatever evaluates this file a second time. */
    if (window.__mlmtCartTabsBound) return;
    window.__mlmtCartTabsBound = true;

    var drawer = null, tablist = null, tabs = [], panels = { cart: null, fav: null };
    var favList = null, favEmpty = null, cartCount = null, products = null, observer = null;
    var current = 'cart';

    /* ── 0 · binding (18 Sep 2026) ───────────────────────────────────
       Everything that used to be captured at the top of this block is
       captured here instead, and captured again whenever #CartDrawer is
       a different node from the one last seen. Same node twice is a
       no-op, so calling this on every section load costs nothing. */
    function bind(){
      var d = document.getElementById('CartDrawer');
      if (!d) return false;
      if (d === drawer) return true;

      var tl = d.querySelector('[role="tablist"]');
      var ts = [].slice.call(d.querySelectorAll('[data-mlmt-tab]'));
      var pc = d.querySelector('#MlmtPanelCart');
      var pf = d.querySelector('#MlmtPanelFav');
      if (!tl || ts.length !== 2 || !pc || !pf) return false;

      drawer = d; tablist = tl; tabs = ts; panels.cart = pc; panels.fav = pf;
      favList   = d.querySelector('[data-mlmt-fav-list]');
      favEmpty  = d.querySelector('[data-mlmt-fav-empty]');
      cartCount = d.querySelector('[data-mlmt-cart-count]');

      if (observer){ observer.disconnect(); observer = null; }
      products = d.querySelector('[data-products]');
      if (products && window.MutationObserver){
        observer = new MutationObserver(onProducts);
        observer.observe(products, { childList: true, subtree: true });
      }
      onProducts();

      /* A fresh render lands on whatever Liquid marked selected; keep
         the tab she was on. */
      select(current);

      /* Published for snippets/mlmt-wishlist-core, which owns the header
         heart. That heart is rendered by header-icons as a plain link and
         has no other way to reach this drawer. Published once, on the first
         successful bind — its presence is what the core routes on. */
      if (!window.mlmtCartTabs) window.mlmtCartTabs = { select: select, open: openTo };
      return true;
    }

    /* ── 1 · switching ───────────────────────────────────────────────
       Nothing here touches the cart form, so switching cannot lose cart
       state: both panels are already in the document and only their
       visibility changes. No navigation, no re-render, no refetch of the
       cart. */
    function select(which){
      if (which !== 'cart' && which !== 'fav') return;
      current = which;
      if (!drawer) return;

      tabs.forEach(function(t){
        var on = t.getAttribute('data-mlmt-tab') === which;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        /* Roving tabindex: one stop for the whole tablist, arrows inside. */
        t.tabIndex = on ? 0 : -1;
      });

      panels.cart.hidden = which !== 'cart';
      panels.fav.hidden  = which !== 'fav';

      if (which === 'fav') favRender();
    }

    /* Arrows move AND activate. Both panels are already built, so there is
       nothing to load and making the shopper press Enter as well would be
       friction with no payoff. Escape is deliberately not handled — the
       theme's own drawer listens for it on window and closes, which is
       what Escape should do in a drawer. */
    document.addEventListener('keydown', function(e){
      if (!tablist || !tablist.contains(e.target)) return;
      var i = tabs.indexOf(document.activeElement);
      if (i < 0) return;

      var next = -1;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % tabs.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + tabs.length) % tabs.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = tabs.length - 1;
      if (next < 0) return;

      e.preventDefault();
      select(tabs[next].getAttribute('data-mlmt-tab'));
      tabs[next].focus();
    });

    /* ── 2 · whoever opened it decides which tab it lands on ─────────
       Capture phase, so the tab has already switched by the time the
       theme's own opener runs and the drawer starts sliding in. */
    document.addEventListener('click', function(e){
      if (e.target.closest && e.target.closest('.js-drawer-open-cart')) select('cart');
    }, true);

    /* Adding a product from a product form is a cart event, and the theme
       opens the drawer on it. Show the cart. (Until 18 Sep 2026 the note
       here said our own Add to cart inside Favorites deliberately did not
       fire this event; it still does not — it calls select('cart') itself,
       see section 4.) */
    document.addEventListener('ajaxProduct:added', function(){ select('cart'); });

    function openTo(which, fallbackHref){
      select(which);

      if (!document.documentElement.classList.contains('js-drawer-open')){
        /* 'cart:open' is the theme's own hook — theme.js labels it "Dev-
           friendly way to open cart" and theme.CartDrawer listens for it.
           It runs synchronously, so if the open class is still missing on
           the very next line then no cart drawer was initialised on this
           template and the link's href is the honest fallback. */
        document.dispatchEvent(new CustomEvent('cart:open'));

        if (!document.documentElement.classList.contains('js-drawer-open')){
          if (fallbackHref) window.location.href = fallbackHref;
          return;
        }
      }

      /* theme.CartDrawer traps focus onto the form 100ms after opening.
         Land her on the tab she actually asked for, after that. */
      var t = tabs.filter(function(x){ return x.getAttribute('data-mlmt-tab') === which; })[0];
      if (t) window.setTimeout(function(){ t.focus(); }, 260);
    }

    /* ── 3 · the count on the Cart tab ───────────────────────────────
       Read out of the markup the theme's ajax cart just injected —
       .cart__items carries data-count and theme.CartForm reads that very
       attribute — rather than from another /cart.js call that could answer
       differently. Two sources for one number is how they drift.

       It is cart.item_count, gift lines and all, because that is what the
       bag bubble in the header counts and these two numbers sit inches
       apart. The gift bar's count is deliberately NOT this one: it counts
       qualifying pieces, so it excludes the gifts the app has already
       granted. Different questions, different numbers, both right. */
    function paintCartCount(){
      if (!cartCount || !drawer) return;
      var items = drawer.querySelector('[data-products] .cart__items');
      if (!items) return;
      var n = parseInt(items.getAttribute('data-count'), 10);
      if (!isFinite(n)) return;
      cartCount.textContent = n;
      if (n > 0) cartCount.removeAttribute('hidden');
      else cartCount.setAttribute('hidden', '');
    }

    /* One observer callback for everything that reads the injected line
       items: the count, and (18 Sep 2026) the heart on each line. */
    function onProducts(){
      paintCartCount();
      decorateLines();
    }

    /* ── 4 · the favorites list ──────────────────────────────────────
       Saved and cart were separate stores that stayed separate: until
       18 Sep 2026 adding a favourite to the cart wrote to the cart and left
       the wishlist untouched, on purpose. REVERSED that day. Jordan: "once
       we add it to cart from favorites, it should auto disappear from
       favorites, as it's in cart of course." So a successful add now takes
       the row off the wishlist — through mlmt-wishlist-core's own remove,
       so every heart on the page repaints and the header count moves —
       and switches the drawer to the Cart tab, where the new line is
       landing. The remove control on the row is no longer the only thing
       that takes a row off this list. The heart on the cart line (section
       5) is the road back. */
    var favSeq = 0;

    function money(cents){
      /* The cart's own formatter, so a price here and the subtotal three
         inches below it can never be in different currencies. */
      if (window.theme && theme.Currency && typeof theme.Currency.formatActiveMoney === 'function'){
        return theme.Currency.formatActiveMoney(cents);
      }
      return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
    }

    function el(tag, cls, text){
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      if (text != null) n.textContent = text;
      return n;
    }

    function productUrl(handle){
      /* theme.routes.home carries the market/locale prefix ('' or '/fr'),
         so a saved handle resolves on the storefront the shopper is
         actually on rather than always on the primary one. */
      var root = (window.theme && theme.routes && theme.routes.home) || '/';
      if (root.charAt(root.length - 1) === '/') root = root.slice(0, -1);
      return root + '/products/' + encodeURIComponent(handle);
    }

    function row(item){
      var node = el('li', 'mlmt-fav__row');
      /* The handle rides on the row so the delegated handlers below can
         find it from any control inside. */
      node.setAttribute('data-handle', item.handle);
      var href = item.url || productUrl(item.handle);

      var link = el('a', 'mlmt-fav__imgw');
      link.href = href;
      var img = document.createElement('img');
      img.alt = ''; img.loading = 'lazy'; img.decoding = 'async';
      if (item.img) img.src = item.img;
      link.appendChild(img);

      var body  = el('div', 'mlmt-fav__body');
      var name  = el('a', 'mlmt-fav__name', item.title || item.handle);
      name.href = href;
      var price = el('p', 'mlmt-fav__price');
      price.appendChild(el('span', 'mlmt-fav__wait', 'Checking price'));
      var add   = el('button', 'mlmt-fav__add', 'Add to cart');
      add.type = 'button';
      add.disabled = true;
      body.appendChild(name); body.appendChild(price); body.appendChild(add);

      var del = el('button', 'mlmt-fav__del');
      del.type = 'button';
      del.setAttribute('aria-label', 'Remove ' + (item.title || item.handle) + ' from favorites');
      del.innerHTML = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" ' +
        'stroke="currentColor" stroke-width="1.7" stroke-linecap="round" aria-hidden="true">' +
        '<path d="M6 6l12 12M18 6 6 18"/></svg>';

      node.appendChild(link); node.appendChild(body); node.appendChild(del);
      return { node: node, img: img, link: link, name: name, price: price, add: add };
    }

    function addToCart(variantId, btn, handle){
      btn.disabled = true;
      btn.textContent = 'Adding';

      /* The theme's own add route, not a hand-typed /cart/add.js, so a
         market or locale prefix on the route is honoured. */
      var url = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';

      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
      .then(function(r){ if (!r.ok) throw new Error('add'); return r.json(); })
      .then(function(){
        /* 'cart:build' is theme.CartForm's own hook — theme.js labels it
           "Dev-friendly way to build the cart". It re-fetches the cart
           markup and repaints the line items, the subtotal and the bag
           bubble, with no reload and without touching this panel.

           'ajaxProduct:added' is deliberately NOT fired: the theme listens
           for it and would run its own buildCart AND open() on top of the
           one we ask for. 'cart:updated' is fired because the blocks that
           read the cart — mlmt-cart-boost, the gift bar, the capsule —
           listen for it. */
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:updated'));

        btn.textContent = 'Added';

        /* 18 Sep 2026 — it is in the cart, so it leaves the favorites. The
           store's own remove fires mlmt:wishlist, which repaints this list
           without the row, then the drawer shows her the bag. Only after a
           2xx: a failed add must not lose the favourite as well. */
        if (handle && window.mlmtWishlist && typeof mlmtWishlist.remove === 'function'){
          mlmtWishlist.remove(handle);
        }
        select('cart');
      })
      .catch(function(){
        /* A 422 here means the variant sold out between the fetch that
           priced this row and the click. Say so, rather than re-arming a
           button that is going to fail the same way again. */
        btn.textContent = 'Unavailable';
      });
    }

    function paint(parts, p, item){
      /* A favourite is saved as a HANDLE, not a variant, so something has
         to choose one — and the only choice that can never put an
         out-of-stock line in the cart is the first variant the product
         itself reports as available. If none is, the row says so and the
         button stays dead rather than adding something unbuyable.

         18 Sep 2026: a record saved from a cart line carries the variant
         she actually had in the bag (vid). If that one is still available
         it wins, so "save for later" and "add it back" round-trip the same
         piece; otherwise the rule above applies unchanged. */
      var v = null, vs = p.variants || [], i;
      var want = item && parseInt(item.vid, 10);
      if (want > 0){
        for (i = 0; i < vs.length; i++){ if (vs[i].id === want && vs[i].available){ v = vs[i]; break; } }
      }
      if (!v) for (i = 0; i < vs.length; i++){ if (vs[i].available){ v = vs[i]; break; } }

      if (p.url){ parts.link.href = p.url; parts.name.href = p.url; }
      if (p.title) parts.name.textContent = p.title;
      var live = p.featured_image || (p.images && p.images[0]);
      if (live) parts.img.src = live;

      parts.price.textContent = '';

      if (!v){
        parts.price.appendChild(el('span', 'mlmt-fav__out', 'Sold out'));
        parts.add.textContent = 'Sold out';
        parts.add.disabled = true;
        return;
      }

      parts.price.appendChild(el('span', 'mlmt-fav__now', money(v.price)));
      /* 19 Sep 2026, Jordan: "Remove discounts from sets" — p is the /products/x.js
         record, whose `type` is the product type; a Bundles product never strikes. */
      if (v.compare_at_price && v.compare_at_price > v.price && p.type !== 'Bundles'){
        parts.price.appendChild(el('s', 'mlmt-fav__was', money(v.compare_at_price)));
      }
      parts.add.disabled = false;
      parts.add.textContent = 'Add to cart';
      /* The chosen variant rides on the button; the click is delegated. */
      parts.add.setAttribute('data-vid', v.id);
    }

    function favRender(){
      if (!favList || !favEmpty) return;

      var list = (window.mlmtWishlist && window.mlmtWishlist.read()) || [];
      /* Every render gets a sequence number. A fetch that lands after the
         list has been rebuilt under it paints a row that is no longer on
         screen, or worse, the wrong one. */
      var seq = ++favSeq;

      favList.innerHTML = '';
      favEmpty.hidden = list.length > 0;
      favList.hidden  = list.length === 0;
      if (!list.length) return;

      list.forEach(function(item){
        if (!item || !item.handle) return;

        var parts = row(item);
        favList.appendChild(parts.node);

        /* THE PRICE IS NEVER READ FROM STORAGE. The saved payload carries
           one — the wishlist page still prints it — but it was captured on
           the day the heart was pressed, and a list of things somebody is
           still deciding about is exactly where a price goes stale. Each
           row asks the product for its own price, so a stale one can be
           neither shown nor added at. */
        fetch(productUrl(item.handle) + '.js', {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' }
        })
        .then(function(r){ if (!r.ok) throw new Error('gone'); return r.json(); })
        .then(function(p){ if (seq === favSeq) paint(parts, p, item); })
        .catch(function(){
          /* 404 means unpublished or deleted since it was saved. */
          if (seq !== favSeq) return;
          parts.price.textContent = '';
          parts.price.appendChild(el('span', 'mlmt-fav__out', 'No longer available'));
          parts.add.textContent = 'Unavailable';
          parts.add.disabled = true;
        });
      });
    }

    /* Only while the panel is on screen. Saving a heart on a collection
       page behind a closed drawer should not start a fetch per favourite.
       The line hearts (section 5) are painted regardless: no fetch, just a
       class per row. */
    document.addEventListener('mlmt:wishlist', function(){
      if (current === 'fav') favRender();
      paintLineHearts();
    });

    /* ── 5 · the heart on every cart line (18 Sep 2026) ──────────────
       Jordan: "in the cart, to the right of the text put the favorite
       button, in case they want to favorite it and don't want to buy it
       in the cart yet, but still save for later."

       The line markup is snippets/cart-item, scraped from
       templates/cart.ajax by theme.CartForm and replaced whole after every
       change — so the heart is not written into that snippet, it is put
       onto each rendered line here, from the observer that already watches
       [data-products] and from 'cart:built'. Idempotent per row: a line
       that already has one is left alone. Gift lines get none — a granted
       gift is not something she chose, so "save it for later" is not a
       sentence that applies to it.

       The click SAVES then REMOVES: the product goes onto the wishlist
       through mlmt-wishlist-core (so the header count and every heart on
       the page move), then the line leaves the cart through the drawer's
       own Remove — the control mlmt-cart-boost puts on the row — so the
       totals, the gift ladder and the bag bubble update exactly as a
       Remove does. If the piece is already a favourite the heart is drawn
       filled and the click only removes the line. The record carries the
       variant she had in the bag, so the Favorites tab's Add to cart puts
       the same piece back. */
    var HEART = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" ' +
      'fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M12 20.3s-7.4-4.6-7.4-9.6a4.2 4.2 0 0 1 7.4-2.7 4.2 4.2 0 0 1 7.4 2.7c0 5-7.4 9.6-7.4 9.6Z"/></svg>';

    function lineHandle(it){
      var a = it.querySelector('.cart__product-name') || it.querySelector('.cart__item--name a');
      var href = a ? (a.getAttribute('href') || '') : '';
      var m = href.match(/\/products\/([^\/?#]+)/);
      if (!m) return null;
      try { return decodeURIComponent(m[1]); } catch(e){ return m[1]; }
    }

    function lineRecord(it){
      /* The same record shape a card heart writes (see the click handler in
         mlmt-wishlist-core), read off the rendered line: the one place the
         cart already prints all of it. */
      var handle = lineHandle(it);
      if (!handle) return null;
      var a   = it.querySelector('.cart__product-name') || it.querySelector('.cart__item--name a');
      var img = it.querySelector('.cart__item--image img');
      var now = it.querySelector('.cart__price:not(.cart__price--strikethrough)');
      var was = it.querySelector('.cart__price--strikethrough');
      var key = it.getAttribute('data-key') || '';
      var vid = parseInt(key.split(':')[0], 10);
      if (!(vid > 0) && now) vid = parseInt(now.getAttribute('data-variant-id'), 10);
      return {
        handle:  handle,
        vid:     vid > 0 ? vid : undefined,
        title:   a ? (a.textContent || '').replace(/\s+/g, ' ').trim() : handle,
        url:     a ? (a.getAttribute('href') || '') : productUrl(handle),
        img:     img ? (img.getAttribute('src') || img.currentSrc || '') : '',
        price:   now ? (now.textContent || '').replace(/\s+/g, ' ').trim() : '',
        compare: was ? (was.textContent || '').replace(/\s+/g, ' ').trim() : ''
      };
    }

    /* 18 Sep 2026, review — THE LABEL SAYS WHAT THE CLICK DOES, PER STATE.
       This is not a toggle, so it carries no aria-pressed: whichever
       state it is in, activating it takes the line out of the bag and
       the control is gone with the row. The first cut painted
       aria-pressed on it, and a saved line then read as "Save for later,
       toggle button, pressed" — a control a screen reader user would
       expect to un-press, that instead removed the piece from the cart
       with nothing announced. Now an unsaved line reads "Save for later"
       (the name the ask gave it) and a saved one reads "Saved — remove
       from cart". The visible heart is unchanged: .is-saved still draws
       it filled. */
    function paintLineHeart(btn){
      var saved = !!(window.mlmtWishlist && typeof mlmtWishlist.has === 'function' &&
                     mlmtWishlist.has(btn.getAttribute('data-handle')));
      btn.classList.toggle('is-saved', saved);
      btn.removeAttribute('aria-pressed');
      btn.setAttribute('aria-label', saved ? 'Saved — remove from cart' : 'Save for later');
    }

    function paintLineHearts(){
      if (!drawer) return;
      [].forEach.call(drawer.querySelectorAll('.mlmt-li-wish'), paintLineHeart);
    }

    function decorateLines(){
      if (!drawer) return;
      [].forEach.call(drawer.querySelectorAll('[data-products] .cart__item'), function(it){
        if (it.hasAttribute('data-gift')) return;
        if (it.querySelector('.mlmt-li-wish')) return;
        var handle = lineHandle(it);
        if (!handle) return;

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mlmt-li-wish';
        btn.setAttribute('data-handle', handle);
        btn.setAttribute('aria-label', 'Save for later');
        btn.innerHTML = HEART;
        paintLineHeart(btn);
        it.appendChild(btn);
      });
    }

    /* The drawer's own remove path. mlmt-cart-boost puts a .mlmt-li-remove
       on every row it has enhanced; pressing it is exactly what Remove
       does — POST cart/change quantity 0, then 'cart:build' + 'cart:updated'
       and its own rebuild. If the row has not been enhanced yet (boost is
       inline in the drawer and can land after us) the same three steps are
       done here by hand, on the theme's own change route. */
    function removeLine(it){
      var rm = it.querySelector('.mlmt-li-remove');
      if (rm && !rm.disabled){ rm.click(); return; }

      var key = it.getAttribute('data-key');
      if (!key) return;
      var url = (window.theme && theme.routes && theme.routes.cartChange) || '/cart/change.js';
      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: key, quantity: 0 })
      })
      .then(function(r){ if (!r.ok) throw new Error('change'); return r.json(); })
      .then(function(cart){
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
      })
      .catch(function(){
        var btn = it.querySelector('.mlmt-li-wish');
        if (btn){ btn.disabled = false; btn.classList.remove('is-busy'); }
      });
    }

    /* ── the drawer's controls, one delegated click ───────────────────
       Tabs, the favourites row's Add to cart and remove, and the line
       heart. All inside #CartDrawer, all type="button" (the form they sit
       in submits to checkout). Bound once on document, so a drawer the
       theme editor re-renders, and line items the ajax cart re-renders,
       keep working without anything being re-bound. */
    document.addEventListener('click', function(e){
      if (!e.target.closest || !drawer) return;

      var tab = e.target.closest('[data-mlmt-tab]');
      if (tab && drawer.contains(tab)){ select(tab.getAttribute('data-mlmt-tab')); return; }

      var del = e.target.closest('.mlmt-fav__del');
      if (del && drawer.contains(del)){
        /* mlmt-wishlist-core owns the store. It writes, it repaints every
           heart on the page and it fires mlmt:wishlist, which brings this
           list back through favRender.

           18 Sep 2026, review — REMOVE ON THE NEXT TICK, NOT INSIDE THIS
           CLICK. Pressing ✕ used to close the whole drawer (since 5 Sep;
           the old per-button listener had the same path). The chain is
           synchronous: remove() → write() → 'mlmt:wishlist' → favRender()
           → favList.innerHTML = '' — which DETACHES the button that was
           clicked while its click is still bubbling. theme.Drawers binds
           its click-outside closer on WINDOW (theme.js, bindEvents) and
           close() tests evt.target.closest('.drawer'); for a detached node
           that is null, so the click reads as "outside" and the drawer
           shuts. Deferring by a tick lets the click finish against a row
           that is still in the document, exactly as the header heart in
           mlmt-wishlist-core defers its open for the same closer.
           stopPropagation() would also work here (this listener is on
           document, the closer on window, so the order is fixed), but
           deferring keeps the click visible to whatever else listens on
           window and asks nothing of listener order. */
        var r1 = del.closest('.mlmt-fav__row');
        var h1 = r1 ? r1.getAttribute('data-handle') : null;
        if (h1 && window.mlmtWishlist && typeof mlmtWishlist.remove === 'function'){
          del.disabled = true;
          window.setTimeout(function(){
            try { mlmtWishlist.remove(h1); } catch(err){ del.disabled = false; }
          }, 0);
        }
        return;
      }

      var add = e.target.closest('.mlmt-fav__add');
      if (add && drawer.contains(add)){
        if (add.disabled) return;
        var vid = parseInt(add.getAttribute('data-vid'), 10);
        var r2 = add.closest('.mlmt-fav__row');
        if (vid > 0) addToCart(vid, add, r2 ? r2.getAttribute('data-handle') : null);
        return;
      }

      var heart = e.target.closest('.mlmt-li-wish');
      if (heart && drawer.contains(heart)){
        e.preventDefault();
        if (heart.disabled) return;
        var it = heart.closest('.cart__item');
        if (!it) return;
        heart.disabled = true;
        heart.classList.add('is-busy');

        var rec = lineRecord(it);
        if (rec && window.mlmtWishlist && typeof mlmtWishlist.add === 'function'){
          /* add() is a no-op on a handle already saved, so a filled heart
             only takes the line out. */
          mlmtWishlist.add(rec);
        }
        paintLineHeart(heart);
        removeLine(it);
      }
    });

    /* 'cart:built' is fired by theme.CartForm after each repaint of the
       lines. The observer above already sees the same paint; this is the
       belt to its braces, for a browser without MutationObserver. */
    document.addEventListener('cart:built', function(){ decorateLines(); });

    /* ── binding: now, and again whenever the editor re-renders ──────── */
    bind();
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
    document.addEventListener('shopify:section:load', function(){ bind(); });
  })();

/* ══ snippets/cart-drawer.liquid — THE ONE WRITER of the subtotal ══ */
  /* THE ONE WRITER of the drawer subtotal (11 Sep 2026). Three scripts used
     to paint [data-subtotal] — Liquid at render, theme.CartForm.cartMarkup
     (final price only) and a DOMContentLoaded fetch here — and the
     strikethrough appeared or not depending on which landed last, then
     vanished after every quantity change. theme.js now leaves the element
     marked data-mlmt-owned alone and fires 'cart:built' with the figures
     after every rebuild; 'cart:updated' is answered from its detail when a
     cart is attached, else from /cart.js (throttled). The load-time fetch is
     gone: Liquid has already painted the same figures and the drawer's first
     build fires 'cart:built' anyway. Paints are sequenced so a slow fetch
     cannot overwrite a newer figure.

     12 Sep 2026: the cart PAGE's subtotal is marked data-mlmt-owned too, so
     this paints every owned [data-subtotal] in the document — drawer and
     page — from the same event and the same formatter. Before, the page's
     Liquid strikethrough vanished after the first quantity change because
     theme.CartForm wrote the final figure alone. */
  (function () {
    var seq = 0;

    function targets() {
      return [].slice.call(document.querySelectorAll('[data-subtotal][data-mlmt-owned]'));
    }

    /* The cart's own formatter, not a hard-coded '$' with two decimals: the
       line items are already in the shopper's currency. */
    function fmt(cents) {
      return (window.theme && theme.Currency && typeof theme.Currency.formatActiveMoney === 'function')
        ? theme.Currency.formatActiveMoney(cents)
        : '$' + (cents / 100).toFixed(2);
    }

    function paint(original, total) {
      if (typeof total !== 'number' || isNaN(total)) return;
      targets().forEach(function (wrap) {
        wrap.innerHTML = '';
        if (typeof original === 'number' && original > total) {
          var originalEl = document.createElement('span');
          originalEl.className = 'cart__original-price';
          originalEl.textContent = fmt(original);
          wrap.appendChild(originalEl);
        }
        var finalEl = document.createElement('span');
        finalEl.className = 'cart__final-price';
        finalEl.textContent = fmt(total);
        wrap.appendChild(finalEl);
      });
    }

    function fromCart(cart) {
      if (!cart || typeof cart.total_price !== 'number') return false;
      seq++;
      paint(cart.original_total_price, cart.total_price);
      return true;
    }

    function fromServer() {
      if (!targets().length) return;
      var mine = ++seq;
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (res) { if (!res.ok) throw res; return res.json(); })
        .then(function (cart) { if (mine === seq) paint(cart.original_total_price, cart.total_price); })
        .catch(function () { /* non-2xx or offline: keep what is there */ });
    }

    document.addEventListener('cart:built', function (e) {
      var d = e.detail || {};
      seq++;
      paint(d.original, d.subtotal);
    });

    /* detail is {cart} from theme.CartForm, a bare cart from quick-add and
       the bundle handler, and nothing at all from the favorites panel. */
    document.addEventListener('cart:updated', function (e) {
      var d = e.detail;
      if (!fromCart(d && (d.cart || d))) fromServer();
    });
  })();
